import React, { useState } from 'react';
import { Dialog } from 'primereact/dialog';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Tag } from 'primereact/tag';
import { Button } from 'primereact/button';
import {
  CourseHourChangeRequestWithDetailsDto,
  HourChangeRequestStatusType,
} from '../../../../../../api/types/programs/course/course-hours-request-change/course-hours-request-change.type';
import { updateHourChangeRequestStatus } from './operations/update-hour-change-request-status';

interface HourChangeRequestsDialogProps {
  visible: boolean;
  onHide: () => void;
  hourChangeRequests: CourseHourChangeRequestWithDetailsDto[];
  onDataRefresh: () => void;
}

export const HourChangeRequestsDialog: React.FC<
  HourChangeRequestsDialogProps
> = ({ visible, onHide, hourChangeRequests, onDataRefresh }) => {
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());
  // Format date
  const formatDate = (date: Date | string | null) => {
    if (!date) return '-';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('he-IL');
  };

  // Get status label
  const getStatusLabel = (status: HourChangeRequestStatusType): string => {
    switch (status) {
      case HourChangeRequestStatusType.PENDING:
        return 'ממתין לאישור';
      case HourChangeRequestStatusType.APPROVED:
        return 'אושר';
      case HourChangeRequestStatusType.DENIED:
        return 'נדחה';
      default:
        return 'לא ידוע';
    }
  };

  // Get status severity
  const getStatusSeverity = (
    status: HourChangeRequestStatusType,
  ): 'success' | 'warning' | 'danger' | 'info' => {
    switch (status) {
      case HourChangeRequestStatusType.APPROVED:
        return 'success';
      case HourChangeRequestStatusType.PENDING:
        return 'warning';
      case HourChangeRequestStatusType.DENIED:
        return 'danger';
      default:
        return 'info';
    }
  };

  // Status body template
  const statusBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    const label = getStatusLabel(rowData.status);
    const severity = getStatusSeverity(rowData.status);
    return <Tag value={label} severity={severity} />;
  };

  // Course details body template
  const courseDetailsBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return `${rowData.courseName} (${rowData.courseHours} שעות)`;
  };

  // Branch body template
  const branchBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return rowData.branchName;
  };

  // Requested hours body template
  const requestedHoursBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return `+${rowData.requestedAdditionalHours} שעות`;
  };

  // Created by body template
  const createdByBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return rowData.createdByName || '-';
  };

  // Created date body template
  const createdDateBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return formatDate(rowData.createdAt);
  };

  // Updated date body template
  const updatedDateBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return formatDate(rowData.updateStatusAt);
  };

  // Justification body template
  const justificationBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    return (
      <div style={{ maxWidth: '300px', wordWrap: 'break-word' }}>
        {rowData.justification || '-'}
      </div>
    );
  };

  // Handle status update
  const handleStatusUpdate = async (
    requestData: CourseHourChangeRequestWithDetailsDto,
    status: HourChangeRequestStatusType,
  ) => {
    setLoadingRows((prev) => new Set(prev).add(requestData.id));
    try {
      await updateHourChangeRequestStatus(
        requestData.programId,
        requestData.courseId,
        requestData.id,
        status,
        () => {
          //
        }, // setLoading is handled by loadingRows state
      );
      onDataRefresh(); // Refresh the data after successful update
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setLoadingRows((prev) => {
        const newSet = new Set(prev);
        newSet.delete(requestData.id);
        return newSet;
      });
    }
  };

  // Actions body template
  const actionsBodyTemplate = (
    rowData: CourseHourChangeRequestWithDetailsDto,
  ) => {
    const isLoading = loadingRows.has(rowData.id);

    return (
      <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
        <Button
          icon="pi pi-check"
          severity="success"
          size="small"
          outlined
          loading={isLoading}
          disabled={isLoading}
          onClick={() =>
            handleStatusUpdate(rowData, HourChangeRequestStatusType.APPROVED)
          }
          tooltip="אשר בקשה"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '32px', height: '32px' }}
        />
        <Button
          icon="pi pi-times"
          severity="danger"
          size="small"
          outlined
          loading={isLoading}
          disabled={isLoading}
          onClick={() =>
            handleStatusUpdate(rowData, HourChangeRequestStatusType.DENIED)
          }
          tooltip="דחה בקשה"
          tooltipOptions={{ position: 'top' }}
          style={{ width: '32px', height: '32px' }}
        />
      </div>
    );
  };

  return (
    <Dialog
      header="בקשות לשינוי במכסת שעות בקורס"
      visible={visible}
      style={{ width: '95vw' }}
      onHide={onHide}
      modal
    >
      <div className="p-4">
        <DataTable
          value={hourChangeRequests}
          loading={false}
          emptyMessage={
            <div style={{ textAlign: 'right' }}>לא נמצאו בקשות לשינוי שעות</div>
          }
          showGridlines
          stripedRows
          responsiveLayout="scroll"
        >
          <Column
            header="פרטי קורס"
            body={courseDetailsBodyTemplate}
            style={{ minWidth: '250px', textAlign: 'right' }}
          />

          <Column
            header="שלוחה"
            body={branchBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />

          <Column
            header="שעות נוספות מבוקשות"
            body={requestedHoursBodyTemplate}
            sortable
            style={{ minWidth: '180px', textAlign: 'right' }}
          />

          <Column
            header="הצדקה"
            body={justificationBodyTemplate}
            style={{ minWidth: '300px', textAlign: 'right' }}
          />

          <Column
            field="status"
            header="סטטוס"
            body={statusBodyTemplate}
            sortable
            style={{ minWidth: '120px', textAlign: 'right' }}
          />

          <Column
            header="נוצר ע״י"
            body={createdByBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />

          <Column
            field="createdAt"
            header="תאריך יצירה"
            body={createdDateBodyTemplate}
            sortable
            style={{ minWidth: '150px', textAlign: 'right' }}
          />

          <Column
            header="פעולות"
            body={actionsBodyTemplate}
            style={{ minWidth: '120px', textAlign: 'center' }}
          />
        </DataTable>
      </div>
    </Dialog>
  );
};
